'use client';

import React from 'react';
import { createPortal } from 'react-dom';

// ─── Dialog Root ────────────────────────────────────────────────────────────────

interface DialogProps {
    isOpen: boolean;
    onClose: () => void;
    /** Prevent closing via backdrop click or the X button */
    preventClose?: boolean;
    /** max-w class: sm → max-w-sm, md → max-w-lg (default), lg → max-w-2xl */
    size?: 'sm' | 'md' | 'lg';
    children: React.ReactNode;
}

const sizeMap: Record<string, string> = {
    sm: 'max-w-sm',
    md: 'max-w-lg',
    lg: 'max-w-2xl',
};

export function Dialog({ isOpen, onClose, preventClose = false, size = 'md', children }: DialogProps) {
    if (!isOpen) return null;

    const handleBackdropClick = () => {
        if (!preventClose) onClose();
    };

    return createPortal(
        <div className="fixed inset-0 z-50 overflow-y-auto" aria-labelledby="dialog-title" role="dialog" aria-modal="true">
            {/* Backdrop */}
            <div
                className="fixed inset-0 bg-black/60 backdrop-blur-sm transition-opacity"
                onClick={handleBackdropClick}
            />

            {/* Centered card */}
            <div className="flex min-h-full items-center justify-center p-4">
                <div className={`relative w-full ${sizeMap[size]} bg-[var(--bg-card)] rounded-xl border border-[var(--border-subtle)] shadow-2xl transform transition-all`}>
                    {children}
                </div>
            </div>
        </div>,
        document.body
    );
}

// ─── Dialog Header ──────────────────────────────────────────────────────────────

interface DialogHeaderProps {
    /** Material Symbols icon name (e.g. "campaign", "warning") */
    icon?: string;
    /** Tailwind text-color class for the icon (default: "text-emerald-400") */
    iconColor?: string;
    /** Tailwind bg class for the icon badge (default: "bg-emerald-500/10") */
    iconBg?: string;
    title: string;
    subtitle?: string;
    onClose?: () => void;
    /** Disable the close button (e.g. during async operations) */
    closeDisabled?: boolean;
}

export function DialogHeader({
    icon,
    iconColor = 'text-emerald-400',
    iconBg = 'bg-emerald-500/10',
    title,
    subtitle,
    onClose,
    closeDisabled = false,
}: DialogHeaderProps) {
    return (
        <div className="px-6 py-5 border-b border-[var(--border-subtle)]">
            <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                    {icon && (
                        <div className={`w-10 h-10 ${iconBg} rounded-lg flex items-center justify-center`}>
                            <span className={`material-symbols-outlined ${iconColor} text-xl`}>{icon}</span>
                        </div>
                    )}
                    <div>
                        <h3 className="font-serif text-xl font-semibold text-[var(--text-primary)]" id="dialog-title">
                            {title}
                        </h3>
                        {subtitle && (
                            <p className="text-sm text-[var(--text-muted)] mt-0.5">{subtitle}</p>
                        )}
                    </div>
                </div>
                {onClose && (
                    <button
                        onClick={onClose}
                        disabled={closeDisabled}
                        className="text-[var(--text-muted)] hover:text-[var(--text-primary)] transition-colors p-1 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                        <span className="material-symbols-outlined text-xl">close</span>
                    </button>
                )}
            </div>
        </div>
    );
}

// ─── Dialog Body ────────────────────────────────────────────────────────────────

interface DialogBodyProps {
    children: React.ReactNode;
    className?: string;
}

export function DialogBody({ children, className = '' }: DialogBodyProps) {
    return (
        <div className={`px-6 py-6 ${className}`}>
            {children}
        </div>
    );
}

// ─── Dialog Footer ──────────────────────────────────────────────────────────────

interface DialogFooterProps {
    children: React.ReactNode;
    className?: string;
}

export function DialogFooter({ children, className = '' }: DialogFooterProps) {
    return (
        <div className={`px-6 py-4 border-t border-[var(--border-subtle)] flex items-center justify-end gap-3 ${className}`}>
            {children}
        </div>
    );
}

// ─── Confirm Dialog ─────────────────────────────────────────────────────────────

interface ConfirmDialogProps {
    isOpen: boolean;
    onClose: () => void;
    onConfirm: () => void;
    title: string;
    message: string;
    confirmLabel?: string;
    cancelLabel?: string;
    /** danger = red confirm button, warning = amber, default = emerald */
    variant?: 'danger' | 'warning' | 'default';
    /** Show a spinner on the confirm button */
    loading?: boolean;
}

const variantStyles: Record<string, { icon: string; iconColor: string; iconBg: string; btnClass: string }> = {
    danger: {
        icon: 'warning',
        iconColor: 'text-red-400',
        iconBg: 'bg-red-500/10',
        btnClass: 'bg-red-500 hover:bg-red-400 text-white hover:shadow-[0_0_20px_rgba(239,68,68,0.3)]',
    },
    warning: {
        icon: 'error',
        iconColor: 'text-amber-400',
        iconBg: 'bg-amber-500/10',
        btnClass: 'bg-amber-500 hover:bg-amber-400 text-[#0A0A0A] hover:shadow-[0_0_20px_rgba(245,158,11,0.3)]',
    },
    default: {
        icon: 'help',
        iconColor: 'text-emerald-400',
        iconBg: 'bg-emerald-500/10',
        btnClass: 'bg-emerald-500 hover:bg-emerald-400 text-[#0A0A0A] hover:shadow-[0_0_20px_rgba(16,185,129,0.3)]',
    },
};

export function ConfirmDialog({
    isOpen,
    onClose,
    onConfirm,
    title,
    message,
    confirmLabel = 'Confirm',
    cancelLabel = 'Cancel',
    variant = 'default',
    loading = false,
}: ConfirmDialogProps) {
    const v = variantStyles[variant];

    return (
        <Dialog isOpen={isOpen} onClose={onClose} preventClose={loading} size="sm">
            <DialogHeader
                icon={v.icon}
                iconColor={v.iconColor}
                iconBg={v.iconBg}
                title={title}
                onClose={onClose}
                closeDisabled={loading}
            />
            <DialogBody>
                <p className="text-sm text-[var(--text-secondary)]">{message}</p>
            </DialogBody>
            <DialogFooter>
                <button
                    onClick={onClose}
                    disabled={loading}
                    className="px-4 py-2.5 text-sm font-medium text-[var(--text-secondary)] hover:text-[var(--text-primary)] transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                    {cancelLabel}
                </button>
                <button
                    onClick={onConfirm}
                    disabled={loading}
                    className={`px-5 py-2.5 rounded-lg font-medium transition-all flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed ${v.btnClass}`}
                >
                    {loading && (
                        <div className="w-4 h-4 border-2 border-current/30 border-t-current rounded-full animate-spin" />
                    )}
                    {confirmLabel}
                </button>
            </DialogFooter>
        </Dialog>
    );
}
